'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["contextualNodeAttributes"],
  _excluded2 = ["contextualNodeAttributes"];
import { RemoveTextStyleIcon } from 'foundations-assets';
import CanvasToolbarButton from 'rich-text-lib/components/toolbar/ToolbarButton';
import tinymce from 'tinymce';
import TinyMceFormatSelect from '../components/toolbar/TinyMceFormatSelect';
import { BLOCK_FORMATS, hsformattingConfig, i18nNameSpace } from '../constants/hsformatting';
import { CANVAS_BLOCK_FORMAT, CANVAS_REMOVE_FORMAT } from '../constants/toolbar';
import { getHsformattingSettings } from '../utils';
import { getListNode, isListParentNode } from '../utils/formatting';
import { i18nWithNameSpace } from '../utils/i18n';
import { jsx as _jsx } from "react/jsx-runtime";
const i18nKey = i18nWithNameSpace(i18nNameSpace);
const removeFormatTooltipi18n = 'clearStyles';
const SECTIONS = {
  paragraph: ['p'],
  headings: ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'],
  other: ['blockquote', 'div', 'pre', 'code']
};
const getBlockquoteFormatHandler = (editor, format = 'blockquote') => () => {
  const selectedNode = editor.selection.getNode();
  let target;

  // Set the target node to the <li> item or the whole list when appropriate
  const listNode = getListNode(editor, selectedNode);
  if (listNode) {
    target = listNode;
  } else if (isListParentNode(selectedNode)) {
    target = selectedNode;
  }
  if (target) {
    editor.undoManager.transact(() => {
      const bm = editor.selection.getBookmark();
      editor.formatter.toggle(format, {}, target);
      editor.selection.moveToBookmark(bm);
      editor.selection.collapse();
    });
  } else {
    editor.execCommand('mceToggleFormat', false, format);
  }
};
function createBlockSelectMenu(styleFormats) {
  return Object.keys(SECTIONS).reduce((items, section) => {
    const includedFormats = styleFormats.filter(styleFormat => SECTIONS[section].indexOf(styleFormat.format) > -1).map(styleFormat => ({
      text: {
        raw: styleFormat.title
      },
      value: styleFormat.format,
      disabled: styleFormat.disabled,
      tooltip: styleFormat.tooltip
    }));
    if (items.length > 0 && includedFormats.length) {
      includedFormats[0].classes = 'new-section';
    }
    return [/* eslint-disable-next-line hubspot-dev/no-reduce-accumulator-copy */
    ...items, ...includedFormats];
  }, []);
}
const FormatToShortcutMap = {
  h1: 'meta+1',
  h2: 'meta+2',
  h3: 'meta+3',
  h4: 'meta+4',
  h5: 'meta+5',
  h6: 'meta+6',
  p: 'meta+7',
  div: 'meta+8'
};
export function addKeyboardShortcuts(editor, blockFormats) {
  blockFormats.forEach(({
    format,
    disabled = false
  }) => {
    if (!disabled && Object.hasOwn(FormatToShortcutMap, format)) {
      editor.addShortcut(FormatToShortcutMap[format], '', ['FormatBlock', false, format]);
    }
  });
}
export function addBlockSelect(editor) {
  editor.addCommand('hsblockquote', getBlockquoteFormatHandler(editor));
  const blockStyleFormats = getHsformattingSettings(editor.settings, hsformattingConfig.BLOCK_FORMATS, BLOCK_FORMATS);
  addKeyboardShortcuts(editor, blockStyleFormats);
  const blockItems = createBlockSelectMenu(blockStyleFormats);
  const formats = blockItems.map(({
    text: {
      raw: text
    },
    value,
    disabled,
    tooltip
  }) => ({
    text: i18nKey(`blockFormats.${text.split(' ').join('').toLowerCase()}`),
    value,
    disabled,
    tooltip
  }));
  tinymce.canvas.addButton(editor, CANVAS_REMOVE_FORMAT, {
    renderControl: props => {
      const rest = _objectWithoutPropertiesLoose(props, _excluded);
      return /*#__PURE__*/_jsx(CanvasToolbarButton, Object.assign({
        "data-test-id": "remove-format-button",
        iconName: "removeTextStyle",
        icon: RemoveTextStyleIcon,
        tooltip: i18nKey(removeFormatTooltipi18n),
        onClick: () => {
          editor.execCommand('RemoveFormat');
        },
        selected: false
      }, rest));
    }
  });
  const getFormattingAttributes = ({
    element,
    parents
  }) => {
    if (!element) {
      return {};
    }
    const matchedFormat = formats.find(({
      value
    }) => editor.formatter.match(value, {}, element));
    const isWithinBlockquote = parents ? parents.find(parent => parent.nodeName === 'BLOCKQUOTE') : null;
    let {
      value = null
    } = matchedFormat || {};
    if (isWithinBlockquote) {
      value = 'blockquote';
    }
    return {
      [CANVAS_BLOCK_FORMAT]: value
    };
  };
  tinymce.canvas.addNodeChangeHandler(editor, {
    getFormattingAttributes
  });
  tinymce.canvas.addButton(editor, CANVAS_BLOCK_FORMAT, {
    renderControl: props => {
      const {
          contextualNodeAttributes
        } = props,
        rest = _objectWithoutPropertiesLoose(props, _excluded2);
      const {
        [CANVAS_BLOCK_FORMAT]: value
      } = contextualNodeAttributes;
      return /*#__PURE__*/_jsx(TinyMceFormatSelect, Object.assign({
        editor: editor,
        value: value,
        formats: formats
      }, rest));
    }
  });
}